
import { ScriptProcessingClient } from "../generated/v1alpha1/Script_processingServiceClientPb";
import { GetScriptInfoRequest, GetScriptInfoResponse, SignedUrlResponse, SignedUrlRequest, ListScriptsRequest, ListScriptsResponse, ProcessScriptRequest, ProcessScriptResponse } from '../generated/v1alpha1/script_processing_pb';
import { AuthServiceClient } from '../generated/v1alpha1/AuthServiceClientPb';
import { RegisterRequest, RegisterResponse, LoginRequest, LoginResponse, LoginWithGoogleRequest, RegisterWithGoogleRequest, RefreshTokenRequest, RefreshTokenResponse, LogoutRequest, LogoutResponse, GetProfileRequest, GetProfileResponse, LinkGoogleAccountRequest, LinkGoogleAccountResponse } from '../generated/v1alpha1/auth_pb';
import { PipelinesServiceClient } from "../generated/v1alpha1/PipelinesServiceClientPb";
import { CreatePipelineRequest, Pipeline, ListPipelinesRequest, ListPipelinesResponse, GetPipelineRequest } from '../generated/v1alpha1/pipelines_pb';
import { Metadata } from "grpc-web";
import { getToken } from "../contexts/tokenStore";

function getAuthMetadata(): Metadata {
  const metadata: Metadata = {};
  const token = getToken();
  if (token) {
    metadata['authorization'] = 'Bearer ' + token;
  }
  return metadata;
}

export class GrpcClientSingleton {
  private static instance: ScriptProcessingClient | null = null;

  private constructor() {}

  public static getInstance(): ScriptProcessingClient {
    if (this.instance === null) {
      const endpoint = process.env.REACT_APP_GRPC_ENDPOINT!;
      console.log(`Connecting to ${endpoint}`)
        this.instance = new ScriptProcessingClient(endpoint, null, { withCredentials: true });
    }
    return this.instance;
  }
}

export class AuthGrpcClientSingleton {
  private static instance: AuthServiceClient | null = null;

  private constructor() {}

  public static getInstance(): AuthServiceClient {
    if (this.instance === null) {
      const endpoint = process.env.REACT_APP_GRPC_ENDPOINT!;
      this.instance = new AuthServiceClient(endpoint, null, { withCredentials: true });
    }
    return this.instance;
  }
}

export class PipelinesGrpcClientSingleton {
  private static instance: PipelinesServiceClient | null = null;

  private constructor() {}

  public static getInstance(): PipelinesServiceClient {
    if (this.instance === null) {
      const endpoint = process.env.REACT_APP_GRPC_ENDPOINT!;
      this.instance = new PipelinesServiceClient(endpoint, null, { withCredentials: true });
    }
    return this.instance;
  }
}

export function createPipeline(request: CreatePipelineRequest): Promise<Pipeline> {
  return new Promise((resolve, reject) => {
    const client = PipelinesGrpcClientSingleton.getInstance();
    client.createPipeline(request, getAuthMetadata(), (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response);
      }
    });
  });
}

export function listPipelines(request: ListPipelinesRequest): Promise<ListPipelinesResponse> {
    return new Promise((resolve, reject) => {
        const client = PipelinesGrpcClientSingleton.getInstance();
        client.listPipelines(request, getAuthMetadata(), (error, response) => {
            if (error) {
                reject(error);
            } else {
                resolve(response);
            }
        });
    });
}

export function getPipeline(request: GetPipelineRequest): Promise<Pipeline> {
    return new Promise((resolve, reject) => {
        const client = PipelinesGrpcClientSingleton.getInstance();
        client.getPipeline(request, getAuthMetadata(), (error, response) => {
            if (error) {
                reject(error);
            } else {
                resolve(response);
            }
        });
    });
}

export function register(request: RegisterRequest): Promise<RegisterResponse> {
  return new Promise((resolve, reject) => {
    const client = AuthGrpcClientSingleton.getInstance();
    client.register(request, {}, (error, response) => {
      if (error) {
        reject(error.message);
      } else {
        resolve(response);
      }
    });
  });
}

export function login(request: LoginRequest): Promise<LoginResponse> {
  return new Promise((resolve, reject) => {
    const client = AuthGrpcClientSingleton.getInstance();
    client.login(request, {}, (error, response) => {
      if (error) {
        reject(error.message);
      } else {
        resolve(response);
      }
    });
  });
}

export function loginWithGoogle(request: LoginWithGoogleRequest): Promise<LoginResponse> {
  return new Promise((resolve, reject) => {
    const client = AuthGrpcClientSingleton.getInstance();
    client.loginWithGoogle(request, {}, (error, response) => {
      if (error) {
        reject(error.message);
      } else {
        resolve(response);
      }
    });
  });
}

export function registerWithGoogle(request: RegisterWithGoogleRequest): Promise<RegisterResponse> {
  return new Promise((resolve, reject) => {
    const client = AuthGrpcClientSingleton.getInstance();
    client.registerWithGoogle(request, {}, (error, response) => {
      if (error) {
        reject(error.message);
      } else {
        resolve(response);
      }
    });
  });
}

export function refreshToken(): Promise<RefreshTokenResponse> {
  return new Promise((resolve, reject) => {
    const client = AuthGrpcClientSingleton.getInstance();
    client.refreshToken(new RefreshTokenRequest(), getAuthMetadata(), (error, response) => {
      if (error) {
        reject(error.message);
      } else {
        resolve(response);
      }
    });
  });
}

export function logout(): Promise<LogoutResponse> {
  return new Promise((resolve, reject) => {
    const client = AuthGrpcClientSingleton.getInstance();
    client.logout(new LogoutRequest(), {}, (error, response) => {
      if (error) {
        reject(error.message);
      } else {
        resolve(response);
      }
    });
  });
}

export function getProfile(request: GetProfileRequest): Promise<GetProfileResponse> {
  return new Promise((resolve, reject) => {
    const client = AuthGrpcClientSingleton.getInstance();
    client.getProfile(request, getAuthMetadata(), (error, response) => {
      if (error) {
        reject(error.message);
      } else {
        resolve(response);
      }
    });
  });
}

export function linkGoogleAccount(request: LinkGoogleAccountRequest): Promise<LinkGoogleAccountResponse> {
  return new Promise((resolve, reject) => {
    const client = AuthGrpcClientSingleton.getInstance();
    client.linkGoogleAccount(request, getAuthMetadata(), (error, response) => {
      if (error) {
        reject(error.message);
      } else {
        resolve(response);
      }
    });
  });
}

export function getSignedUrl(request: SignedUrlRequest): Promise<SignedUrlResponse> {
  return new Promise((resolve, reject) => {
    const client = GrpcClientSingleton.getInstance();
    client.getSignedUrl(request, getAuthMetadata(), (error, response) => {
      if (error) {
        reject(error.message);
      } else {
        resolve(response);
      }
    });
  });
}

export function processScript(request: ProcessScriptRequest): Promise<ProcessScriptResponse> {
  return new Promise((resolve, reject) => {
    const client = GrpcClientSingleton.getInstance();
    client.processScript(request, getAuthMetadata(), (error, response) => {
      if (error) {
        reject(error);
      } else {
        resolve(response);
      }
    });
  });
}

export function getScriptInfo(request: GetScriptInfoRequest): Promise<GetScriptInfoResponse> {
  return new Promise((resolve, reject) => {
    const client = GrpcClientSingleton.getInstance();
    client.getScriptInfo(request, getAuthMetadata(), (error, response) => {
      if (error) {
        reject(error.message);
      } else {
        resolve(response);
      }
    });
  });
}

export function listScripts(request: ListScriptsRequest): Promise<ListScriptsResponse> {
  return new Promise((resolve, reject) => {
    const client = GrpcClientSingleton.getInstance();
    client.listScripts(request, getAuthMetadata(), (error, response) => {
      if (error) {
        reject(error.message);
      } else {
        resolve(response);
      }
    });
  });
}
